import { create } from 'zustand';
import { CoachingIntervention, CoachingScript } from '@/types';
import { coachingService } from '@/services/coachingService';

interface CoachingState {
  interventions: CoachingIntervention[];
  scripts: CoachingScript[];
  isCoachingEnabled: boolean;
  loading: boolean;
  
  // Actions
  loadScripts: () => Promise<void>;
  loadInterventions: () => Promise<void>;
  triggerIntervention: (
    type: CoachingIntervention['interventionType'],
    context: CoachingIntervention['context']
  ) => Promise<void>;
  dismissIntervention: (id: string) => Promise<void>;
  respondToIntervention: (id: string, response: string) => Promise<void>;
  setCoachingEnabled: (enabled: boolean) => void;
  getScriptByKey: (key: string) => CoachingScript | null;
}

export const useCoachingStore = create<CoachingState>((set, get) => ({
  interventions: [],
  scripts: [],
  isCoachingEnabled: true,
  loading: false,

  loadScripts: async () => {
    try {
      const scripts = await coachingService.getActiveScripts();
      set({ scripts });
    } catch (error) {
      console.error('Erro ao carregar scripts:', error);
    }
  },

  loadInterventions: async () => {
    set({ loading: true });
    try {
      const interventions = await coachingService.getActiveInterventions();
      set({ interventions, loading: false });
    } catch (error) {
      console.error('Erro ao carregar intervenções:', error);
      set({ loading: false });
    }
  },

  triggerIntervention: async (type, context) => {
    const { isCoachingEnabled } = get();
    if (!isCoachingEnabled) return;

    try {
      // Buscar script apropriado
      const scriptKey = getScriptKeyForType(type);
      const script = await coachingService.getScriptByKey(scriptKey);
      
      if (!script) {
        console.warn(`Script não encontrado para tipo: ${type}`);
        return;
      }

      // Criar intervenção
      const id = await coachingService.createIntervention(
        type,
        context,
        script.scriptKey
      );

      const newIntervention: CoachingIntervention = {
        id,
        userId: '',
        interventionType: type,
        context,
        scriptTemplate: script.scriptKey,
        dismissed: false,
        createdAt: new Date().toISOString()
      };

      set(state => ({
        interventions: [newIntervention, ...state.interventions]
      }));
    } catch (error) {
      console.error('Erro ao criar intervenção:', error);
    }
  },

  dismissIntervention: async (id) => {
    try {
      await coachingService.dismissIntervention(id);
      set(state => ({
        interventions: state.interventions.map(i =>
          i.id === id ? { ...i, dismissed: true } : i
        )
      }));
    } catch (error) {
      console.error('Erro ao dispensar intervenção:', error);
    }
  },

  respondToIntervention: async (id, response) => {
    try {
      await coachingService.logUserResponse(id, response);
      set(state => ({
        interventions: state.interventions.map(i =>
          i.id === id ? { ...i, userResponse: response } : i
        )
      }));
    } catch (error) {
      console.error('Erro ao responder intervenção:', error);
    }
  },

  setCoachingEnabled: (enabled) => {
    set({ isCoachingEnabled: enabled });
  },

  getScriptByKey: (key) => {
    const { scripts } = get();
    return scripts.find(s => s.scriptKey === key) || null;
  }
}));

// Helper para mapear tipo de intervenção para chave de script
function getScriptKeyForType(type: CoachingIntervention['interventionType']): string {
  const map: Record<CoachingIntervention['interventionType'], string> = {
    failure_support: 'failure_gentle',
    success_reinforcement: 'success_identity',
    adjustment_suggestion: 'low_adherence',
    reflection_prompt: 'reflection_weekly'
  };
  return map[type] || 'failure_gentle';
}
